import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Clock, Sparkles, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PREMIUM_KEY_STORAGE = "fp_premium_key";

function formatRemaining(expiresAt) {
  if (!expiresAt) return null;
  const exp = new Date(expiresAt).getTime();
  const now = Date.now();
  const diff = exp - now;
  if (Number.isNaN(diff) || diff <= 0) return { expired: true, h: 0, m: 0, s: 0 };
  const h = Math.floor(diff / 3600_000);
  const m = Math.floor((diff % 3600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1000);
  return { expired: false, h, m, s };
}

/**
 * PremiumCountdown — Self-contained badge.
 * Renders nothing if no premium key in localStorage OR key is invalid/admin (no expiry).
 * Auto-refreshes every second for live countdown.
 */
export default function PremiumCountdown({ variant = "compact" }) {
  const [keyStatus, setKeyStatus] = useState(null);
  const [, setTick] = useState(0);

  const loadStatus = useCallback(async () => {
    const k = (typeof window !== "undefined" && localStorage.getItem(PREMIUM_KEY_STORAGE)) || "";
    if (!k) {
      setKeyStatus(null);
      return;
    }
    try {
      const r = await axios.get(`${API}/payments/key-status`, {
        params: { key: k },
        timeout: 10000,
      });
      setKeyStatus(r.data || null);
    } catch {
      setKeyStatus(null);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    // Refresh status from API every 60s (server may revoke / extend)
    const apiInt = setInterval(loadStatus, 60_000);
    // Re-render local countdown every second
    const tickInt = setInterval(() => setTick((t) => t + 1), 1000);
    // Listen to storage changes (e.g. after upgrade in another tab)
    const onStorage = (e) => {
      if (e.key === PREMIUM_KEY_STORAGE) loadStatus();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      clearInterval(apiInt);
      clearInterval(tickInt);
      window.removeEventListener("storage", onStorage);
    };
  }, [loadStatus]);

  if (!keyStatus || !keyStatus.valid) return null;
  // Admin / lifetime key has no expires_at — don't show countdown
  if (!keyStatus.expires_at) return null;

  const r = formatRemaining(keyStatus.expires_at);
  if (!r) return null;
  if (r.expired) {
    return (
      <div
        className="inline-flex items-center gap-2 border-2 border-black bg-[#FF2400] text-white px-3 py-1.5 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.18em]"
        data-testid="premium-countdown-expired"
        title="Premium key sudah expired. Beli ulang untuk akses Premium."
      >
        <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} />
        Premium · Expired
      </div>
    );
  }

  const isUrgent = r.h === 0 && r.m < 60; // last hour
  const isWarning = r.h < 3;
  const bg = isUrgent ? "bg-[#FF2400] text-white" : isWarning ? "bg-[#FFD700] text-[#0a0a0a]" : "bg-[#0a0a0a] text-[#FFD700]";

  // Compact (header) layout: "◆ Premium · 18h 23m"
  if (variant === "compact") {
    const display = r.h > 0
      ? `${r.h}h ${String(r.m).padStart(2, "0")}m`
      : `${r.m}m ${String(r.s).padStart(2, "0")}s`;
    return (
      <div
        className={`inline-flex items-center gap-2 border-2 border-black ${bg} px-3 py-1.5 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.18em] font-bold`}
        data-testid="premium-countdown-compact"
        title={`Premium aktif. Expires: ${new Date(keyStatus.expires_at).toLocaleString("id-ID")}`}
      >
        <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
        Premium · sisa {display}
      </div>
    );
  }

  // Full banner (e.g. above form): with seconds & label
  return (
    <div
      className={`border-2 border-black ${bg} px-4 py-3 flex items-center gap-3 font-mono uppercase tracking-[0.18em] text-xs md:text-sm font-bold`}
      data-testid="premium-countdown-banner"
    >
      <Sparkles className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2.5} />
      <span>Premium aktif · sisa</span>
      <span className="font-display font-black tabular-nums tracking-tight text-lg md:text-2xl normal-case">
        {String(r.h).padStart(2, "0")}
        <span className="opacity-60">h</span>{" "}
        {String(r.m).padStart(2, "0")}
        <span className="opacity-60">m</span>{" "}
        {String(r.s).padStart(2, "0")}
        <span className="opacity-60">s</span>
      </span>
      <Clock className="w-4 h-4 ml-auto" strokeWidth={2.5} />
    </div>
  );
}
